import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import sha256 from 'crypto-js/sha256';
import * as CryptoJS from 'crypto-js';
@Component({
  selector: 'app-change-password-form',
  standalone: true,
  imports: [
    InputTextModule,
    FormsModule,
    ReactiveFormsModule,
    PasswordModule,
    CommonModule,
    ButtonModule,
    SvgIconComponent,
  ],
  templateUrl: './change-password-form.component.html',
  styleUrl: './change-password-form.component.scss',
})
export class ChangePasswordFormComponent implements OnInit {
  @Output() closePasswordDialogEvent = new EventEmitter();
  changePasswordForm: FormGroup = new FormGroup({});
  constructor(
    private _auth: AuthService,
    private formBuilder: FormBuilder,
    private router: Router,
    private messageService: MessageService
  ) {
    effect(() => {
      this._auth.userDetail();
      this.checkChangePassword();
    });
  }
  currentUrl = this.router.url;
  isFirstTime = this.currentUrl.includes('change-password');
  isNewUser: boolean =
    this._auth.userDetail().project === null &&
    !this._auth.userDetail().isPasswordUpdatedByUser &&
    !this._auth.userDetail().isTfaConfigured;
  isPasswordChecked: boolean = false;
  ngOnInit(): void {
    this.getUserDetails();
    if (this.isFirstTime) {
      this.changePasswordForm = new FormGroup({
        newPassword: new FormControl(null, [
          Validators.required,
          this.passwordStrength(),
        ]),
        confirmPassword: new FormControl(null, [Validators.required]),
      });
      this.checkChangePassword();
    } else {
      this.changePasswordForm = new FormGroup({
        currentPassword: this.formBuilder.control(null, [Validators.required]),
        newPassword: new FormControl(null, [
          Validators.required,
          this.passwordStrength(),
        ]),
        confirmPassword: new FormControl(null, [Validators.required]),
      });
    }
  }

  passwordPolicy = [
    {
      name: 'capitalLetter',
      displayName: 'At least one uppercase letter',
      isPassed: false,
      regex: '(?=.*[A-Z])',
    },
    {
      name: 'smallLetter',
      displayName: 'At least one lowercase letter',
      isPassed: false,
      regex: '(?=.*[a-z])',
    },
    {
      name: 'number',
      displayName: 'At least one digit',
      isPassed: false,
      regex: '(?=.*[0-9])',
    },
    {
      name: 'passwordLength',
      displayName: 'At least 10 characters long',
      isPassed: false,
      regex: '',
    },
    {
      name: 'specialCharacter',
      displayName: 'At least 1 special character',
      isPassed: false,
      regex: '(?=.*\\W)',
    },
  ];

  passwordStrength(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let password = control.value;

      for (
        let policyIndex = 0;
        policyIndex < this.passwordPolicy.length;
        policyIndex++
      ) {
        if (password && password.length > 0) {
          if (this.passwordPolicy[policyIndex].name === 'passwordLength') {
            this.passwordPolicy[policyIndex].isPassed =
              String(password).length >= 10 ? true : false;
          } else {
            let regex: string = this.passwordPolicy[policyIndex].regex;
            this.passwordPolicy[policyIndex].isPassed = new RegExp(regex).test(
              password
            );
          }
        } else {
          this.passwordPolicy[policyIndex].isPassed = false;
        }
      }
      let result = this.passwordPolicy.filter((ele) => ele.isPassed === false)
        .length
        ? true
        : false;
      return result
        ? {
            passwordStrength: {
              value: control.value,
              message: 'Weak Password',
            },
          }
        : null;
    };
  }

  checkPasswordMatch() {
    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    const confirmPassword =
      this.changePasswordForm.get('confirmPassword')?.value;

    if (
      newPassword !== null &&
      newPassword !== undefined &&
      newPassword === confirmPassword
    ) {
      return true;
    } else {
      return false;
    }
  }

  isChangingPassword: boolean = false;
  changePassword() {
    if (this.changePasswordForm.invalid) {
    this.isChangingPassword = false;

      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please fill all the fields correctly',
        key: 'tr',
      });
      this.changePasswordForm.markAllAsTouched();
      return;
    }

    this.isChangingPassword = true;

    if (this.isFirstTime) {
      this.createPassword();
    } else {
      this.updatePassword();
    }
  }

  async createPassword() {
    let salt = await this.getUpdateSalt();
    if (salt) {
      const passwordHistoryHash = CryptoJS.SHA256(
        this.changePasswordForm.get('newPassword')?.value
      ).toString(CryptoJS.enc.Hex);
      const passwordHash = sha256(
        this.changePasswordForm.get('newPassword')?.value + salt
      ).toString(CryptoJS.enc.Hex);
      let body = {
        hash: passwordHistoryHash,
        password: passwordHash,
      };
      let param = {
        id: this._auth.userDetail()._id,
      };
      this._auth.createPassword(param, body).subscribe({
        next: (response) => {
          this.isChangingPassword = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Password created successfully',
            key: 'tr',
          });
          this.changePasswordForm.reset();

          this._auth.userDetail().isPasswordUpdatedByUser = true;

          if (this.isNewUser) {
            this.closePasswordDialogEvent.emit();
          } else {
            localStorage.removeItem('token');
            this.router.navigate(['']);
          }
        },
        error: (error) => {
          console.log('error', error);
          this.isChangingPassword = false;
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: error.error.message,
            key: 'tr',
          });
        },
      });
    }
  }

  async updatePassword() {
    let salt = await this.getSalt();

    if (salt) {
      const currentPasswordHash = sha256(
        this.changePasswordForm.get('currentPassword')?.value + salt
      ).toString(CryptoJS.enc.Hex);
      const passwordHistoryHash = CryptoJS.SHA256(
        this.changePasswordForm.get('newPassword')?.value
      ).toString(CryptoJS.enc.Hex);
      const newPasswordHash = sha256(
        this.changePasswordForm.get('newPassword')?.value + salt
      ).toString(CryptoJS.enc.Hex);
      let body = {
        currentPassword: currentPasswordHash,
        hash: passwordHistoryHash,
        password: newPasswordHash,
      };
      let param = {
        id: this._auth.userDetail()?._id,
      };
      this._auth.updatePassword(param, body).subscribe({
        next: (response) => {
          this.isChangingPassword = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Password updated successfully',
            key: 'tr',
          });
          this.changePasswordForm.reset();
          this.closePasswordDialogEvent.emit();
        },
        error: (error) => {
          console.log('error', error);
          this.isChangingPassword = false;
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: error.error.message,
            key: 'tr',
          });
        },
      });
    }
  }

  resetForm() {
    this.changePasswordForm.reset();
  }

  checkChangePassword() {
    if (
      this.router.url?.includes('change-password') &&
      this._auth.userDetail().isPasswordUpdatedByUser
    ) {
      this.router.navigate(['']);
    }
  }

  isCurrentPasswordEqualsNewPassword() {
    const currentPass = this.changePasswordForm.value.currentPassword;
    const newPass = this.changePasswordForm.value.newPassword;

    if (currentPass === null || currentPass === undefined || this.isFirstTime) {
      return false;
    } else if (newPass === currentPass) {
      return true;
    } else {
      return false;
    }
  }

  getUserDetails = () => {
    this._auth.getUserDetails().subscribe({
      next: (res: any) => {
        this._auth.userDetail.set(res);
      },
    });
  };

  getSalt = async () => {
    let params = {
      _id: this._auth.userDetail()._id,
    };
    let response: any = await this._auth.getSalt(params);
    return response.salt;
  };

  getUpdateSalt = async () => {
    let body = {
      email: this._auth.userDetail().email,
    };
    let response: any = await this._auth.updateSalt(body);
    return response.salt;
  };
}
